// Per-workspace settings + personalization.
//
// `root.workspaceSettings` is the per-workspace settings bag. It carries
// `home_default_panels` plus these per-workspace personalization fields, all
// resolved from the server workspace record's `settings` bag (ONE store —
// `Workspace.settings`, written via the merge-safe sdk.updateWorkspaceSettings.
// A separate `workspace_dashboard_defaults` row used to double as a second
// home_default_panels source; retired 2026-07-13, see loadPrivateData.js
// _dashboardPrefsFetches and tickets/workspace.md "home_default_panels
// dual-store fight"):
//
//   designSystem        — personalized look (canvas bg + color/shadow overrides)
//   language            — BCP-47 lang for <html lang>
//   layoutDirection     — 'ltr' | 'rtl' for <html dir>
//   navbar              — ordered HeaderTabs arrangement; default Studio/Tasks/Files
//   modules             — enabled workspace module ids
//   home_default_panels — visible home dashboard widget ids
//   apps                — apps-menu section entries
//
// PRECEDENCE (resolveWorkspaceConfig): the unpacked workspace PROJECT
// (root.workspaceProject.config — functions/workspaceProjects.js, the
// integration-based source of truth) ▸ the Mongo workspace record `settings`
// bag (org-guarded, generic per-workspace personalization) ▸ DEFAULT_CONFIG
// (framework default). No workspace is special-cased in this file.
//
// A workspace's config is APPLIED when it becomes active — see
// applyWorkspaceConfig, driven from loadPrivateData (boot) + onWorkspaceChange,
// which call loadWorkspaceProject first so the project is in context. The skin
// reverts to the framework default as part of that activate-new cycle
// (_applyDesignSystem reverts the previous workspace's vars before applying).

import _wsShared from '@symbo-ls/workspace-shared'
const shellSetCanvasBackgroundVar = (...args) => _wsShared.functions.setCanvasBackgroundVar(...args)
const shellClearCanvasBackgroundVar = (...args) => _wsShared.functions.clearCanvasBackgroundVar(...args)
const shellResolveCanvasBackgroundTheme = (...args) =>
  _wsShared.functions.resolveCanvasBackgroundTheme(...args)
const shellWorkspaceSettings = (...args) => _wsShared.functions.workspaceSettings(...args)
const shellResolveWorkspaceThemeMode = (...args) =>
  _wsShared.functions.resolveWorkspaceThemeMode(...args)
const shellUserHasOwnThemeChoice = (...args) => _wsShared.functions.userHasOwnThemeChoice(...args)
const shellHasNoStoredThemeMode = (...args) => _wsShared.functions.hasNoStoredThemeMode(...args)
// Opt-in boot breadcrumbs (`?trail=1`) — inert otherwise. See bootTrail.js
// for why a wedged renderer cannot be diagnosed by any post-hoc probe.
// Optional-call on purpose, unlike its load-bearing neighbours above: this
// is a diagnostic, and it is called at the ENTRY of applyWorkspaceConfig,
// outside any try. If a stale shared build ever resolves without it, a
// plain call would throw there and take workspace activation down with it —
// a debugging aid that can break the boot is worse than no aid at all.
const shellBootTrail = (...args) => _wsShared.functions.bootTrail?.(...args)
import { fillLauncherTileFrom } from './launcherTiles.js'
import { memberCan } from './moduleAccessBridge.js'
// THE app-mode predicate (record REF, via the spec §5 settings accessor).
// See `_isAppModeInstall` below for why this file no longer restates it.
import { isAppModeWorkspace } from './homeDefaultWidgets.js'

// ── Navbar arrangement ───────────────────────────────────────────────────────
// Each entry is a HeaderTabs tab: { label, path }. `path` must resolve to a
// PageSlot route (registered in pages/main.js _APP_WINDOW_PAGES + searchApps
// APP_WINDOW_ROUTES). The default keeps the house Studio / Tasks / Files set.
//
// IMPORTANT — no `key` field. HeaderTabs renders these via childrenAs: 'state',
// and DOMQL uses a data object's `key` property as the collection's
// reconciliation identity. A `key` shared between arrangements (e.g. `tickets`,
// present in both Studio/Tasks/Files and GreenIG) makes DOMQL REUSE that tab
// node in place across a live navbar swap — leaving it pinned at its old index
// so the new order renders scrambled (Tasks stuck to the front instead of
// Policies leading). Without `key`, reconciliation is positional and the new
// arrangement renders in array order. Identity belongs to `path`, which the
// active-pill matcher already keys off.
//
// i18n — `label` IS A POLYGLOT KEY HERE, and a tenant's label is NOT.
// This list is SHIPPED CHROME, so it takes keys like any other chrome string
// (docs/I18N-CONVENTION.md). Its words are rendered by HeaderTab/MobileTab as
// `el.call('polyglot', s.label)`, so a key resolves to the session language.
// The SAME render site also prints a workspace's OWN navbar rows, whose labels
// are the customer's content ("CRM", "Ops", a Georgian phrase). Those must
// survive verbatim, and they do, without a branch anywhere: polyglot's
// `translate()` returns THE KEY ITSELF on a miss, and the shipped keys are
// namespaced dotted paths that are members of the flat `<namespace>.json` map
// while a customer's words are not. Membership of the bundle is the whole
// distinction — there is no marker prop and no "does this look like a key"
// heuristic to drift out of sync. Pinned both ways in
// __tests__/navbarLabelI18n.test.js.
// A key added here must exist in EVERY shipped language bundle; `shell` is a
// boot namespace (shared/context.js + bootShell.js BOOT_NAMESPACES), so these
// three resolve on the first paint.
//
// The first tab, "Apps", opens /apps — the ONE catalogue: the workspace's own
// apps and the App Store tab (Nika's decision 7). It opened /studio, the
// Websites board, until 2026-10-25: a business person who pressed "Apps" to
// find an app landed in a developer studio (persona audit round 1, ledger
// AS-3). The label key is unchanged, so the word is unchanged in every
// language; the Websites board stays one click away in the Apps views.
export const DEFAULT_NAVBAR = [
  { label: 'shell.app.studio', path: '/apps' },
  { label: 'shell.app.tasks', path: '/tasks' },
  { label: 'shell.app.files', path: '/files' }
]

// ── Personalized design system ───────────────────────────────────────────────
// A design-system preset re-skins the workspace by overriding CSS custom
// properties on <html> at runtime (the revertible channel canvasBackground.js
// uses) — no scratch config mutation, so it deactivates/reactivates cleanly on
// workspace switch. `vars` are raw `--prop: value` overrides; `canvasBg` rides
// the dedicated --canvas-bg channel so every canvas Stage + glass card retints.
// GROUND: the ONE per-workspace ground override is `vars['--stage-bg']`
// (shared/functions/canvasBackground.js STAGE_BACKGROUND, BF-WIDGET-INK-1) —
// it reaches the shell root, home page + board and every canvas Stage
// together. `--home-bg` has NO reader any more (the home board dropped it
// 2026-08-16 — a second per-workspace ground channel re-created the seam on
// ws_UYAOhQyNjm); a record that still carries it is inert. `canvasBg`
// (--canvas-bg, canvas only) stays ABOVE the chain: it is the user's own
// Zoombar pick, a different actor, and it splits canvas from root on purpose.
// Per-workspace design systems now live in the workspace PROJECT
// (functions/workspaceProjects.js), unpacked into root.workspaceProject — they
// are NOT hardcoded here. This file owns only the framework default + the
// generic apply/revert machinery + the Mongo-settings precedence.
export const DEFAULT_DESIGN_SYSTEM = null // null = framework default (no overrides)

export const DEFAULT_CONFIG = {
  navbar: DEFAULT_NAVBAR,
  designSystem: DEFAULT_DESIGN_SYSTEM,
  language: 'en',
  // 'default' | 'enforced' (workspace-shared functions/languages.js
  // DEFAULT_LANGUAGE_POLICY). Since 2026-09-10 the two RENDER THE SAME: the
  // workspace's own `language` decides its interface language for every
  // member, and a workspace that sets none renders English. A member's own
  // account preference and this device's stored choice outrank nothing
  // inside a workspace (resolveLang ignores both), and setLang refuses a
  // switch the workspace's resolved language would revert. Carried through
  // this file for the admin card that reads `root.workspaceSettings`; the
  // RESOLUTION itself lives in `enforcedWorkspaceLang` + `resolveLang`, never
  // here.
  languagePolicy: 'default',
  layoutDirection: 'ltr',
  modules: null
}

const _lc = (v) => (v == null ? '' : String(v).toLowerCase())

// THE installed-app entries of a workspace settings bag — the ONE reader every
// consumer calls (moved here from workspaceProjects.js `_appRefs`, unchanged,
// so the loader, the config compose and the home all agree). settings
// .workspaceApps also holds rows that are NOT apps (the server's records-grant
// `legacy-backfill` row) — the records plane reads THOSE through its own grant
// read (moduleDataBridge), never through this.
//   * non-objects (strings included) are skipped;
//   * an AI-built extension ({ type: 'extension', projectId }) is normalized to
//     owner 'extension', key projectId, so the shared owner/key identity holds;
//   * an entry with NO type, or type 'project', with owner + key IS an app
//     (untyped legacy entries stay apps);
//   * deduped by lower-cased owner/key, the last write wins, in stored order.
// Self-contained (FA204): no module-scope helper is reached from here.
export const installedAppRefs = (settings) => {
  const lc = (v) => (v == null ? '' : String(v).toLowerCase())
  const idOf = (ref) => `${lc(ref && ref.owner)}/${lc(ref && ref.key)}`
  const list = settings && Array.isArray(settings.workspaceApps) ? settings.workspaceApps : []
  const byId = new Map()
  for (const c of list) {
    if (!c || typeof c !== 'object') continue
    if (c.type === 'extension' && c.projectId) {
      const norm = { owner: 'extension', key: String(c.projectId), ...c }
      byId.set(idOf(norm), norm)
      continue
    }
    if ((!c.type || c.type === 'project') && c.owner && c.key) {
      byId.set(idOf(c), c)
    }
  }
  return [...byId.values()]
}

// Recursive structural equality for the activation no-op guards (boot F4 —
// tickets/workspace.md BOOT-RESHUFFLE root cause 4). Module activation re-runs
// up to 4× per boot (boot chain, auth ensure, onWorkspaceChange, converge
// retries) and every pass re-published `workspaceProject`/`workspaceApps`/
// `workspaceSettings` even when the recomputed output was IDENTICAL — each
// write re-rendered the navbar + home grid + design-system consumers
// post-reveal. The publishes now skip when the computed value deep-equals what
// is already live. Deliberately NOT a JSON.stringify comparison (key order is
// not guaranteed and functions/undefined silently vanish):
//   - key-order independent (own enumerable keys, both directions)
//   - functions compare by IDENTITY — module payloads are memoized per
//     owner/key@version, so a re-pass carries the same fn refs; a genuinely
//     new module build yields new refs and correctly publishes
//   - Date by timestamp; NaN equals NaN; null and undefined stay DISTINCT
//   - cycle-guarded: a revisited pair reports UNEQUAL, so a cyclic value
//     falls back to publishing (same behavior as before the guard)
export const shellDeepEqual = (a, b, seen = null) => {
  if (a === b) return true
  if (typeof a === 'number' && typeof b === 'number') return Number.isNaN(a) && Number.isNaN(b)
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false
  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime()
  }
  if (Array.isArray(a) !== Array.isArray(b)) return false
  if (seen) {
    for (const [sa, sb] of seen) {
      if (sa === a && sb === b) return false
    }
  } else {
    seen = []
  }
  seen.push([a, b])
  const ka = Object.keys(a)
  const kb = Object.keys(b)
  if (ka.length !== kb.length) return false
  for (const k of ka) {
    if (!Object.prototype.hasOwnProperty.call(b, k)) return false
    if (!shellDeepEqual(a[k], b[k], seen)) return false
  }
  return true
}

// ── Legacy navbar-label migration (i18n, 2026-09-02) ─────────────────────────
// DEFAULT_NAVBAR's labels used to be the English WORDS; they are polyglot KEYS
// now. A workspace whose admin saved the navbar through the module console
// before that change has the old words persisted in Mongo `settings.navbar`,
// and those rows have to keep behaving like the shipped default — otherwise
// `_sameNavbar` below stops recognising them, the bare-default guard in
// applyWorkspaceConfig flips them to "a deliberate personalization", and a
// module's own navbar can never win again (the exact failure that guard was
// written for). They must also render translated, like any other house row.
//
// The match is a (path, exact-old-word) PAIR, so it is as tight as it can be:
// a workspace that RENAMED /studio to "Design Lab", or that named some other
// route "Studio", is untouched and stays the customer's content. This is a
// migration table — it encodes history, not copy — so the English words are
// frozen here on purpose and must never grow a fourth entry for a NEW default
// (a new default ships with its key from day one).
const _LEGACY_NAVBAR_LABEL_KEYS = {
  '/studio': { Studio: 'shell.app.studio' },
  '/tasks': { Tasks: 'shell.app.tasks' },
  '/files': { Files: 'shell.app.files' }
}

// ── The shipped "Apps" tab moved (2026-10-25, ledger AS-3) ──────────────────
// DEFAULT_NAVBAR's first tab opens /apps now (see its header). A workspace
// that stored the OLD shipped row (`shell.app.studio` on /studio) stored the
// bare default, so that row moves with the default — otherwise `_sameNavbar`
// would read it as a deliberate personalization and a module's own bar could
// never win again (the bare-default guard in applyWorkspaceConfig). Like the
// table above, the match is the exact shipped (path, label) pair: a tab a
// person renamed or pointed elsewhere is theirs and stays untouched.
const _MOVED_NAVBAR_ROWS = {
  '/studio': { 'shell.app.studio': '/apps' }
}

const _canonicalNavbar = (nav) => {
  if (!Array.isArray(nav)) return nav
  let changed = false
  const out = nav.map((tab) => {
    const key = tab && _LEGACY_NAVBAR_LABEL_KEYS[tab.path]?.[tab.label]
    const row = key ? { ...tab, label: key } : tab
    const movedTo = row && _MOVED_NAVBAR_ROWS[row.path]?.[row.label]
    if (!key && !movedTo) return tab
    changed = true
    return movedTo ? { ...row, path: movedTo } : row
  })
  // Identity is preserved when nothing migrated, so the deep-equal no-op guard
  // in applyWorkspaceConfig is unaffected on the common (already-key) path.
  return changed ? out : nav
}

// Shallow navbar equality by ordered (path, label) pairs. Used to tell a real
// personalization from the bare shell default — see the navbar precedence in
// applyWorkspaceConfig.
const _sameNavbar = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
  return a.every((t, i) => t && b[i] && t.path === b[i].path && t.label === b[i].label)
}

// Stale-safety gate. `root.workspace` can lag `root.org` across an ORG switch
// (cross-org stale-workspace leak; see MEMORY cross-org-workspace-leak) — after
// switching away, root.workspace can STILL read the previous workspace doc while
// root.org is already the new org. So a Mongo `settings` bag is only trusted when
// the workspace record's parent org matches the active org.
const _workspaceBelongsToActiveOrg = (root) => {
  const org = root.org || {}
  const ws = root.workspace || {}
  const orgId = _lc(org.id || org._id)
  const wsOrg = _lc(ws.organization || ws.organizationId || ws.org)
  return !!orgId && !!wsOrg && orgId === wsOrg
}

// Resolve the active workspace's config. PRECEDENCE:
//   1) the unpacked workspace PROJECT (root.workspaceProject.config) — the
//      integration-based source of truth for a project-backed workspace
//      (skin/navbar/panels/apps/modules). Set by loadWorkspaceProject at init.
//   2) the Mongo workspace record's `settings` bag (org-guarded) — generic
//      per-workspace personalization for workspaces with no project.
//   3) DEFAULT_CONFIG (framework default).
// No workspace is special-cased here — a workspace gets a non-default config
// ONLY because its project (or Mongo settings) says so.
// Additive (app-mode) compose — a marketplace / add-app install must ADD the
// app to the workspace, never take it over (2026-07-23 per Nika: installing
// crm-app wiped the default navbar down to just "Contacts"). The install paths
// mark their ref `installMode: 'app'` (workspaceModuleRef.js); refs written by
// the admin module console / direct settings (client-workspace modules like
// greenig-app, bellforge-app, financials-app) carry no flag and keep the
// full-takeover semantics below. App-mode:
//   navbar               → base default + the app's entries APPENDED (deduped
//                          by path; a saved personalization still wins later
//                          in applyWorkspaceConfig's precedence)
//   home_default_panels  → NOT taken from the app (existing dashboard stays;
//                          the app's widgets are still offered via
//                          homeCatalogFor / Customize)
//   apps / everything else → the app's values (workspace-scoped surfaces)
// DECISION site (spec §5): "is the installed module app-mode or full-takeover?"
// Absent and unknown lead to the same branch — full-takeover semantics — and
// this is only ever consulted when `root.workspaceProject.config` already
// exists, i.e. after loadWorkspaceProject resolved the ref (which does its own
// full-record fetch when the bag is missing). So it does NOT gate on `ready`.
// It still reads through the accessor so the record's shape is respected: a
// list-shaped record must contribute nothing here rather than an `undefined`
// that reads identically to "no module".
// ONE rule, ONE place. This expression used to live here AND, restated from
// memory against the wrong plane, in functions/homeDefaultWidgets.js — where it
// read `root.workspaceSettings.workspaceModule` (the RESOLVED CONFIG bag this
// very file publishes, which never carries that key) and was inert on every
// real workspace. Delegating removes the second opinion: whatever decides that
// an app-mode install strips `home_default_panels` here is the same function
// that decides the home board's default widget set.
const _isAppModeInstall = (root) => isAppModeWorkspace(root)

const _appendNavbar = (base, extra) => {
  const out = Array.isArray(base) ? base.slice() : []
  const seen = new Set(out.map((t) => _lc(t && t.path)))
  for (const tab of Array.isArray(extra) ? extra : []) {
    if (!tab || seen.has(_lc(tab.path))) continue
    seen.add(_lc(tab.path))
    out.push(tab)
  }
  return out
}

// ── The installed app's OWN identity, stamped onto the rows it contributes ──
//
// A row an app contributes (`configDefaults.navbar` / `.apps`, published on the
// app's manifest) carries only `{ label, path }` — WORDS, and no identity at
// all. So the shell had no way to ask "which app is this tab?" and the tab
// printed the manifest's English literal for good: "Warehouse" in the navbar
// while the App Store called the same record "Inventory / ინვენტარი".
//
// `appKey` is that missing identity: the store key of the app the row STANDS
// FOR, taken from the app's own `appId` (`system/warehouse-app` -> the record
// key `warehouse-app`, which is exactly what the store resolves its copy off).
// The render sites then read the label through THE store-copy resolver
// (`shared/functions/storeItemText.js`, `el.call('storeItemText', { key:
// row.appKey, name: row.label }, 'name')`) instead of printing the literal.
// One app, one name, one resolver — the shell never learns a second rule, and
// a third party's app or an extension keeps its own name for free, because
// `translate()` returns its input unchanged for a key nobody ships.
//
// ONE ROW ONLY, and this is a contract, not a shortcut: the app's store name
// names the APP. It can stand for a ROW only when the app contributes exactly
// one row of that kind. An app that contributes several is naming its own
// SUB-SURFACES (policies-management ships `Policies` + `Claims` on one appId),
// and no app-level name can be right for any single one of them — stamping
// them all would rename "Claims" to "Insurance Policy Dashboard". Unstamped
// rows keep their published words, exactly as today.
const _appRowsWithKey = (rows, appId) => {
  if (!Array.isArray(rows) || rows.length !== 1 || !rows[0]) return rows
  const key = String(appId || '')
    .split('/')
    .pop()
  if (!key) return rows
  // A NEW object — an app's manifest rows are shared state, never mutated.
  return [{ ...rows[0], appKey: key }]
}

// An AI extension's rows — ONE builder for the loaded entry (workspaceProjects.js
// loadWorkspaceApps) and for the server's `nav` copy below, so both paint the
// same path, label and tile and a row never moves when the app loads.
export const extensionAppRows = ({ key, projectId, name }) => {
  const path = '/x/' + (key || String(projectId))
  return {
    navbar: [{ label: name, path }],
    apps: [{ title: name, route: path, projectId: String(projectId), kind: 'extension' }]
  }
}

// Boot fix step 3a — the server-owned `nav` copy on each settings.workspaceApps[]
// entry (core app-nav contract): the rows an app contributes, readable at boot
// before its module loads. App content: only whitelisted string / number fields
// ride into a row (rendered as text by the same row consumers as a loaded app's).
const _navText = (v) => (typeof v === 'string' ? v : '')
const _navPick = (src, keys) => {
  const out = {}
  for (const k of keys) {
    const v = src[k]
    if (typeof v === 'string' || (typeof v === 'number' && Number.isFinite(v))) out[k] = v
  }
  return out
}
const _navRows = (ref, appId) => {
  const nav = ref && ref.nav
  if (!nav || typeof nav !== 'object' || nav.v !== 1) return null
  if (ref.type === 'extension') {
    const name = _navText(nav.name)
    if (!name) return null
    return { appId, ...extensionAppRows({ key: _navText(nav.key), projectId: ref.projectId, name }) }
  }
  const navbar = (Array.isArray(nav.navbar) ? nav.navbar : [])
    .filter((r) => r && _navText(r.path) && _navText(r.label))
    .map((r) => _navPick(r, ['label', 'path', 'icon', 'order', 'permission']))
  const apps = (Array.isArray(nav.apps) ? nav.apps : [])
    .filter((t) => t && _navText(t.route) && _navText(t.title))
    .map((t) =>
      _navPick(t, ['title', 'caption', 'icon', 'iconBg', 'glyph', 'route', 'order', 'permission'])
    )
  if (!navbar.length && !apps.length) return null
  return { appId, navbar, apps }
}
// The rows every installed app contributes, in INSTALL order: a loaded app
// (root.workspaceApps) speaks for itself; an app that has not loaded yet speaks
// through its entry's `nav`; an entry without `nav` adds nothing until it loads.
// Only the CURRENT workspace's full record is read (provenance + org guard), so
// a switch never paints the previous workspace's rows.
const _appRowSources = (root) => {
  const published = Array.isArray(root && root.workspaceApps) ? root.workspaceApps : []
  const { settings, ready } = shellWorkspaceSettings(root)
  const list =
    ready && settings && Array.isArray(settings.workspaceApps) && _workspaceBelongsToActiveOrg(root)
      ? settings.workspaceApps
      : null
  if (!list) return published
  const byId = new Map()
  for (const a of published) if (a && a.appId) byId.set(_lc(a.appId), a)
  const out = []
  // THE installed-app entries (installedAppRefs — the loader's own reader):
  // extensions come normalized to owner 'extension', key projectId; a row
  // that is not an app (the records-grant legacy-backfill row) never appears.
  for (const ref of installedAppRefs(settings)) {
    const id = _lc(ref.owner) + '/' + _lc(ref.key)
    const live = byId.get(id)
    if (live) out.push(live)
    else {
      const fromNav = _navRows(ref, id)
      if (fromNav) out.push(fromNav)
    }
  }
  return out
}

// Append EVERY additive app's navbar entries (root.workspaceApps — loaded by
// loadWorkspaceApps) onto a base navbar, deduped by path. This is what makes
// installing app B not clobber app A: each app contributes its tabs on top of
// the base, in install order. Order is stable (the array preserves it).
// A row or tile an app gates on a permission key (MODULE-NAV-PERMISSION-1,
// manifest `permission: '<key>'` on configDefaults.navbar[i] / .apps[i]):
// shown only when the member holds the key — moduleCan's answer, so owners and
// admins always see it and an unknown member does not until the workspace
// record says. A hidden row also takes a stored copy of itself (same path)
// out of the base navbar, so a saved arrangement cannot bring it back.
const _gated = (item) =>
  !!item && typeof item.permission === 'string' && item.permission.trim() !== ''
const _hiddenFor = (root, item) => _gated(item) && !memberCan(root, item.permission)

const _appendAppsNavbar = (base, root) => {
  let nav = base
  for (const app of _appRowSources(root)) {
    const rows = app && _appRowsWithKey(app.navbar, app.appId)
    const list = Array.isArray(rows) ? rows : []
    const hidden = new Set(list.filter((r) => _hiddenFor(root, r)).map((r) => _lc(r.path)))
    if (hidden.size)
      nav = (Array.isArray(nav) ? nav : []).filter((t) => !hidden.has(_lc(t && t.path)))
    nav = _appendNavbar(
      nav,
      list.filter((r) => !hidden.has(_lc(r && r.path)))
    )
  }
  return nav
}

// Union of every additive app's launcher tiles (config.apps) — appended to the
// base launcher list, deduped so the app-drawer shows each installed app once.
//
// The identity MUST cover `route`/`title`, not just `path`/`label`: a manifest-v2
// launcher tile is `{ title, caption, icon, iconBg, route }` (every app in
// workspace/apps/* uses that shape), so a path||label key was always falsy for
// them and the dedupe never fired. Re-resolving over an already-appended base
// then duplicated every tile — ICCA Exams' 8 tiles resolved as 16 (2026-07-25).
const _tileKey = (t) => _lc((t && (t.route || t.path)) || (t && (t.title || t.label)))

const _appendAppsLauncher = (base, root) => {
  const out = Array.isArray(base) ? base.slice() : []
  const idxByKey = new Map()
  out.forEach((t, i) => {
    const k = _tileKey(t)
    if (k && !idxByKey.has(k)) idxByKey.set(k, i)
  })
  const hiddenTiles = new Set()
  for (const app of _appRowSources(root)) {
    const appTiles = _appRowsWithKey(app && app.apps, app && app.appId)
    for (const tile of Array.isArray(appTiles) ? appTiles : []) {
      if (!tile) continue
      const key = _tileKey(tile)
      if (_hiddenFor(root, tile)) {
        if (key) hiddenTiles.add(key)
        continue
      }
      if (key && idxByKey.has(key)) {
        // A base row (a stored settings.apps entry) already claims this
        // identity. It used to WIN the dedupe outright — so a thin stored row
        // (bare { route }, no title/icon) shadowed the installed app's own
        // manifest tile and the launcher rendered a blank tile. Keep the
        // stored row's slot + deliberate overrides, but backfill its MISSING
        // display meta from the richer manifest tile (returns a new object;
        // state rows are never mutated).
        const i = idxByKey.get(key)
        out[i] = fillLauncherTileFrom(out[i], tile)
        continue
      }
      if (key) idxByKey.set(key, out.length)
      out.push(tile)
    }
  }
  // A stored copy of a hidden tile (same route / title) goes too.
  return hiddenTiles.size ? out.filter((t) => !hiddenTiles.has(_tileKey(t))) : out
}

export const resolveWorkspaceConfig = (root) => {
  if (!root) return DEFAULT_CONFIG

  // Loaded apps, or installed apps whose rows the record's `nav` already carries.
  const hasApps = _appRowSources(root).length > 0

  // 1) Workspace project (the ONE full-takeover module, unpacked into context by
  //    loadWorkspaceProject). Its config is the base; additive apps still append.
  const project = root.workspaceProject
  if (project && project.config) {
    // Legacy single-app-stored-as-module (pre-plural: installMode:'app' on
    // workspaceModule). Base = DEFAULT, the module's own navbar appended, home
    // panels NOT taken — then any plural apps append on top too.
    if (_isAppModeInstall(root)) {
      const cfg = { ...DEFAULT_CONFIG, ...project.config }
      // A STORED arrangement wins (Nika 2026-10-11: "remove apps tasks files.
      // move training as first, decisions second"): the record's
      // settings.navbar — full record, active org — IS the bar; installed
      // additive apps still append. A stored copy of the bare default is no
      // personalization. applyWorkspaceConfig's "saved" is the derived
      // root.workspaceSettings cache, never the record, so this is the one
      // place the stored bar can win (__tests__/appModeStoredNavbar.test.js).
      const { settings: stored, ready: storedReady } = shellWorkspaceSettings(root)
      const storedNav =
        storedReady && stored && _workspaceBelongsToActiveOrg(root)
          ? _normalizeSavedRow(stored).navbar
          : null
      const personal = storedNav && !_sameNavbar(storedNav, DEFAULT_NAVBAR) ? storedNav : null
      // A module page NOT in the stored bar appends at the END, so a page the
      // module adds later still shows; the shell defaults never come back.
      cfg.navbar = _appendAppsNavbar(
        _appendNavbar(personal || DEFAULT_NAVBAR, project.config.navbar),
        root
      )
      cfg.apps = _appendAppsLauncher(project.config.apps, root)
      delete cfg.home_default_panels
      return cfg
    }
    // Real full-takeover client module (greenig/bellforge/financials): its
    // config wins — skin, home panels, navbar — but additive apps still append
    // their navbar tabs + launcher tiles on top (they never take over).
    const cfg = { ...DEFAULT_CONFIG, ...project.config }
    if (hasApps) {
      cfg.navbar = _appendAppsNavbar(cfg.navbar || DEFAULT_NAVBAR, root)
      cfg.apps = _appendAppsLauncher(cfg.apps, root)
    }
    return cfg
  }

  // 2) No takeover module. Base = Mongo workspace `settings` (org-guarded) or
  //    DEFAULT_CONFIG. Additive apps append their navbar + launcher on top.
  //
  // §5 RENDER SOURCE — this is where "the workspace hasn't loaded" turned into
  // "the workspace is configured with the framework default". `ws.settings` was
  // read directly; on a list-shaped record it is `undefined`, the `if` below
  // never fires, and the function returns DEFAULT_CONFIG — which
  // applyWorkspaceConfig then PUBLISHES as `root.workspaceSettings.navbar`,
  // making the default indistinguishable from a real choice for every
  // downstream reader. Reading through the accessor makes the two cases
  // separable; the caller (applyWorkspaceConfig) is what acts on it.
  const { settings, ready } = shellWorkspaceSettings(root)
  let base = null
  if (ready && settings && typeof settings === 'object' && _workspaceBelongsToActiveOrg(root)) {
    const fromMongo = _normalizeSavedRow(settings)
    // ANY personalization key qualifies the bag as a base — not just
    // designSystem/navbar. The old two-key gate silently discarded a settings
    // bag that carried ONLY home_default_panels (e.g. an additive-apps
    // workspace pinning its module widgets: settings = { workspaceApps,
    // home_default_panels }), so the authoritative panel set never reached
    // root.workspaceSettings and the home dashboard force-hid every pinned
    // module widget (verified live on natali 2026-07-25). _normalizeSavedRow
    // already whitelists the keys and drops null/empty values, so an empty or
    // junk bag still falls through to DEFAULT_CONFIG.
    if (Object.keys(fromMongo).length) {
      base = { ...DEFAULT_CONFIG, ...fromMongo }
    }
  }
  if (!base) {
    if (!hasApps) return DEFAULT_CONFIG
    base = { ...DEFAULT_CONFIG }
  }

  if (hasApps) {
    base.navbar = _appendAppsNavbar(base.navbar || DEFAULT_NAVBAR, root)
    base.apps = _appendAppsLauncher(base.apps, root)
  }
  return base
}

// ── Apply / revert the personalized design system at runtime ─────────────────
// All overrides go on <html> inline style so they win over the :root sheet and
// revert cleanly via removeProperty. We track what we set so revert is exact.
const _appliedVars = new Set()

// BOOT-RESHUFFLE F4 — re-applying an IDENTICAL design system is a no-op. The
// activation chain calls applyWorkspaceConfig up to four times per boot; each
// call reverted + re-set every <html> css var (a live document-level swap) even
// when nothing changed. Fingerprint the outgoing ds; unfingerprintable → apply.
let _lastDsFp = null

// The workspace-driven theme currently in force, or null. Mirrors the
// _lastDsFp/_revertDesignSystem pattern: this module owns ONE workspace
// override at a time and must be able to take it back on a switch, otherwise
// a light workspace would leak its theme into the next workspace.
let _appliedWorkspaceTheme = null

// The design system currently in force, or null. `vars` is resolved PER THEME
// (below), so the record has to stay readable after the apply pass: the
// data-theme watcher repaints from this, never from a re-run of the whole
// config chain.
let _activeDesignSystem = null

// ── Per-theme record vars (FW-DESIGN-SYSTEM-RECORD-NEEDS-PER-THEME-VARS-1) ──
// `vars` carries ONE value per token, so a record can deliver only one half of
// a token whose light and dark values differ. When both halves are the same
// TYPE a tenant can write `light-dark(a, b)` and the browser picks. When they
// are DIFFERENT types that door is shut: `light-dark()` takes two <color>s, so
// a gradient half makes the substituted `background` invalid at
// computed-value time and the element paints NOTHING —
// `--theme-secondary-{,hover,active,isActive}-background`,
// `--theme-tertiary-{hover,active,isActive}-background` and
// `--theme-primary-gradient-background` are all gradient-in-light,
// colour-in-dark. Two per-theme channels open it, and a record may use either:
//   designSystem.varsLight / designSystem.varsDark  — { '--prop': value }
//   designSystem.vars['--prop'] = { light, dark }   — the same fact, inline
// PRECEDENCE, per prop: varsLight/varsDark (this theme) > a { light, dark }
// pair > a plain `vars` value. A prop whose ONLY declaration is the other
// theme's is left unset, so the stylesheet's own value shows through rather
// than the wrong half. A pair with one half missing behaves the same way.
export const resolveDesignSystemVars = (ds, theme) => {
  const out = {}
  if (!ds || typeof ds !== 'object') return out
  const half = theme === 'light' ? 'light' : 'dark'
  const isPair = (v) =>
    !!v && typeof v === 'object' && !Array.isArray(v) && ('light' in v || 'dark' in v)
  const take = (prop, value) => {
    if (value == null || value === '') return
    out[prop] = String(value)
  }
  const vars = ds.vars && typeof ds.vars === 'object' && !Array.isArray(ds.vars) ? ds.vars : {}
  for (const [prop, value] of Object.entries(vars)) take(prop, isPair(value) ? value[half] : value)
  const perTheme = half === 'light' ? ds.varsLight : ds.varsDark
  if (perTheme && typeof perTheme === 'object' && !Array.isArray(perTheme)) {
    for (const [prop, value] of Object.entries(perTheme)) take(prop, value)
  }
  return out
}

// Write the half of `_activeDesignSystem` that belongs to the theme in force
// right now. Idempotent: a prop both halves declare is re-set, never removed
// and re-added, so a theme flip cannot blink it.
const _paintDesignSystemVars = (doc = globalThis.document) => {
  const rootEl = doc?.documentElement
  if (!rootEl) return
  const next = resolveDesignSystemVars(
    _activeDesignSystem,
    shellResolveCanvasBackgroundTheme(undefined, doc)
  )
  for (const prop of _appliedVars) {
    if (!(prop in next)) rootEl.style.removeProperty(prop)
  }
  _appliedVars.clear()
  for (const [prop, value] of Object.entries(next)) {
    rootEl.style.setProperty(prop, value)
    _appliedVars.add(prop)
  }
}

// One document-level watcher that repaints the record's per-theme vars on
// EVERY `data-theme` flip — user-driven (`changeGlobalTheme`) and OS-driven
// (scratch's own prefers-color-scheme listener under 'auto'). Same shape and
// the same reason as shared/functions/canvasBackground.js
// `watchCanvasBackgroundTheme`: the attribute is the one effect both causes
// produce, so one MutationObserver catches both with no framework hook.
// Singleton per document; lives for the app's lifetime by design.
export const watchDesignSystemTheme = (doc = globalThis.document) => {
  const rootEl = doc?.documentElement
  if (!rootEl) return null
  if (doc.__wsDesignSystemThemeWatcher) return doc.__wsDesignSystemThemeWatcher
  const Observer = doc.defaultView?.MutationObserver || globalThis.MutationObserver
  if (!Observer) return null
  const observer = new Observer(() => _paintDesignSystemVars(doc))
  observer.observe(rootEl, { attributes: true, attributeFilter: ['data-theme'] })
  doc.__wsDesignSystemThemeWatcher = observer
  return observer
}

const _applyDesignSystem = (ds, doc = globalThis.document) => {
  const rootEl = doc?.documentElement
  if (!rootEl) return
  // Always start from a clean slate so switching A→B doesn't leave A's vars.
  _revertDesignSystem(doc)
  _activeDesignSystem = ds || null
  if (!ds) {
    shellClearCanvasBackgroundVar(doc)
    return
  }
  if (ds.canvasBg) shellSetCanvasBackgroundVar(doc, ds.canvasBg)
  else shellClearCanvasBackgroundVar(doc)
  _paintDesignSystemVars(doc)
  watchDesignSystemTheme(doc)
}

const _revertDesignSystem = (doc = globalThis.document) => {
  const rootEl = doc?.documentElement
  if (!rootEl) return
  for (const prop of _appliedVars) rootEl.style.removeProperty(prop)
  _appliedVars.clear()
  // The watcher stays (it is a document singleton) but must find nothing to
  // repaint, or a theme flip would put the reverted workspace's vars back.
  _activeDesignSystem = null
}

// Normalize a SAVED workspace_settings row (snake_case columns from the DB) to
// the camelCase shape the rest of the app consumes. Only fields the row actually
// carries are returned, so they can override the registry preset selectively.
const _normalizeSavedRow = (ws) => {
  if (!ws || typeof ws !== 'object') return {}
  const out = {}
  // navbar: null AND [] both mean "cleared, fall through to the preset/
  // default" — an empty tab list is never a real personalization (it renders
  // a header with ZERO links). Same guard as HeaderTabs' readers and
  // workspaceProjects.js's config merge.
  // `_canonicalNavbar` migrates the three pre-i18n English house labels back to
  // their polyglot keys; every other row is returned untouched (see the table).
  if (Array.isArray(ws.navbar) && ws.navbar.length) out.navbar = _canonicalNavbar(ws.navbar)
  // design system: accept either camelCase (already-applied) or snake_case (row).
  if (ws.designSystem != null) out.designSystem = ws.designSystem
  else if (ws.design_system != null) out.designSystem = ws.design_system
  if (ws.language != null) out.language = ws.language
  if (ws.languagePolicy != null) out.languagePolicy = ws.languagePolicy
  if (ws.layoutDirection != null) out.layoutDirection = ws.layoutDirection
  else if (ws.layout_direction != null) out.layoutDirection = ws.layout_direction
  if (ws.modules != null) out.modules = ws.modules
  if (ws.home_default_panels != null) out.home_default_panels = ws.home_default_panels
  if (ws.apps != null) out.apps = ws.apps
  return out
}

export const applyWorkspaceConfig = (root, doc = globalThis.document) => {
  if (!root) return
  shellBootTrail('workspaceConfig:apply', root.workspace?.key || root.workspace?.id || '?')
  // `workspaceSettings` is a derived, root-level cache. Its contents are only
  // meaningful for the workspace that produced them; treating a previous
  // workspace's bag as a user override is what let a late System pass win over
  // the freshly-resolved workspace's module/navbar/panels during boot. Every
  // successful application stamps the bag below, so once a bag has provenance
  // it is an explicit ownership boundary rather than a best-effort hint.
  //
  // Keep unscoped bags for the current invocation as a migration bridge: older
  // in-place personalization flows created them before the stamp existed. They
  // are immediately restamped below. A stamped mismatch, however, is never
  // carried forward — a new workspace must resolve from its own record/module.
  const activeWorkspaceId = String(root.workspace?.id ?? root.workspace?._id ?? '')
  const heldSettings = root.workspaceSettings
  const heldWorkspaceId = String(heldSettings?.workspace_id ?? '')
  const scopedSettings =
    heldSettings &&
    typeof heldSettings === 'object' &&
    (!heldWorkspaceId || (activeWorkspaceId && heldWorkspaceId === activeWorkspaceId))
      ? heldSettings
      : {}
  // PRECEDENCE (field-by-field): whatever root.workspaceSettings ALREADY holds
  // (its own prior value — e.g. an admin console's optimistic in-flight edit,
  // written ahead of the network round-trip by dashboard-defaults.js /
  // moduleConsole.js mcSavePersonalization) > the Mongo workspace record
  // `settings` bag (the actual source of truth, returned by
  // resolveWorkspaceConfig) > the frontend registry preset > DEFAULT_CONFIG.
  // This is a self-merge, not a second store: root.workspaceSettings is
  // populated ONLY by this function (from Mongo) plus the two admin consoles'
  // optimistic writes of the SAME `Workspace.settings` keys — never a separate
  // row (the legacy `workspace_dashboard_defaults` row that used to double as
  // one was retired 2026-07-13; see loadPrivateData.js _dashboardPrefsFetches).
  const preset = resolveWorkspaceConfig(root)
  const saved = _normalizeSavedRow(scopedSettings)
  // Language is resolved from the workspace record, not the previously
  // derived settings bag. Self-merging that bag pinned an old language after
  // a full-record refresh, even when the shared resolver had already switched
  // the rendered interface. Keep the same precedence as boot and live policy.
  const { settings: languageSettings, ready: languageReady } = shellWorkspaceSettings(root)
  const languageScopeReady = languageReady && _workspaceBelongsToActiveOrg(root)
  const liveLanguageSettings = languageScopeReady ? languageSettings : null
  const resolvedLanguage = _wsShared.functions.resolveLang({
    workspaceEnforced: _wsShared.functions.enforcedWorkspaceLang(liveLanguageSettings),
    workspaceDefault: liveLanguageSettings?.language || null,
    brandDefault: root.whitelabelBrand?.lang || null
  })

  // Navbar precedence with a default-guard. Unlike home_default_panels/apps
  // (written below ONLY when present), applyWorkspaceConfig ALWAYS writes a
  // navbar back into root.workspaceSettings — including the bare DEFAULT_NAVBAR
  // when it runs BEFORE the workspace project loads (boot race:
  // loadWorkspaceProject hadn't populated root.workspaceProject yet, so the
  // preset fell through to DEFAULT_CONFIG). On the next run that stale default
  // is now `saved.navbar` and, under a plain `saved.navbar || preset.navbar`,
  // pins Studio/Tasks/Files over the module's own navbar forever. So a saved
  // navbar that is merely the bare default is NOT treated as a personalization:
  // when the resolved preset carries a NON-default navbar (a project/module
  // ships its own), the preset wins. A saved navbar that DIFFERS from the
  // default is a deliberate reorder and is always kept. (Module-console reset
  // writes navbar:null, not the default array, so it also falls to the preset.)
  const savedNavbarIsBareDefault = saved.navbar && _sameNavbar(saved.navbar, DEFAULT_NAVBAR)
  const presetHasCustomNavbar = preset.navbar && !_sameNavbar(preset.navbar, DEFAULT_NAVBAR)
  // Installed additive apps ALWAYS contribute their tabs on top of whichever
  // navbar wins — appending an installed app is not a personalization question.
  // Without this, a workspace whose SAVED navbar is a module's custom set (any
  // takeover-module workspace, e.g. bellforge) could never surface a
  // just-installed app's tab: the saved navbar wins the precedence below, and
  // only the PRESET carried the append (verified live 2026-07-24 — "Add to
  // Workspace" wrote the ref + loaded the app but no tab appeared). Deduped by
  // path, so a navbar that already carries the tabs is untouched.
  // DERIVED vs PERSONAL (ws crm review of e713f38ea). The bag this function
  // publishes carries the bar it derived (`_navbarDerived`, below). A cached
  // bar equal to that stamp came from an EARLIER pass, not from a person; in
  // app mode such a bar gives way to the fresh resolve — otherwise a pass that
  // ran before the org resolved (P12) or on a lean record (P3) pinned
  // default + module tabs and the record's stored bar never showed. An
  // optimistic console edit changes the bar but not the stamp, so it still
  // wins. Other modes keep their precedence unchanged.
  const derivedStamp = Array.isArray(scopedSettings._navbarDerived)
    ? scopedSettings._navbarDerived
    : null
  const savedIsDerived = !!(
    saved.navbar &&
    derivedStamp &&
    _sameNavbar(saved.navbar, derivedStamp) &&
    _isAppModeInstall(root)
  )
  const navbar = _appendAppsNavbar(
    !saved.navbar || (savedNavbarIsBareDefault && presetHasCustomNavbar) || savedIsDerived
      ? preset.navbar
      : saved.navbar,
    root
  )

  const cfg = {
    navbar,
    designSystem: saved.designSystem !== undefined ? saved.designSystem : preset.designSystem,
    // `workspace.settings.theme = { mode }` — an object, not a bare string,
    // so a later accent/logo can join without a migration.
    theme: saved.theme !== undefined ? saved.theme : preset.theme,
    language: resolvedLanguage,
    languagePolicy:
      liveLanguageSettings?.languagePolicy || _wsShared.functions.DEFAULT_LANGUAGE_POLICY,
    layoutDirection: saved.layoutDirection || preset.layoutDirection,
    modules: saved.modules || preset.modules,
    home_default_panels: saved.home_default_panels || preset.home_default_panels,
    // Same always-append rule as the navbar above — installed apps' launcher
    // tiles surface regardless of which apps list won the precedence.
    apps: _appendAppsLauncher(saved.apps || preset.apps, root)
  }

  // Design system — apply (or revert to framework default when designSystem is
  // null/absent). This is the deactivate-old / activate-new cycle. Skipped
  // when the ds is byte-identical to the one already applied (see _lastDsFp).
  let _dsFp = null
  try {
    _dsFp = JSON.stringify(cfg.designSystem ?? null)
  } catch (_) {
    _dsFp = null
  }
  if (!(_dsFp && _dsFp === _lastDsFp)) {
    _lastDsFp = _dsFp
    shellBootTrail('workspaceConfig:designSystem')
    _applyDesignSystem(cfg.designSystem, doc)
  }

  // Workspace DEFAULT theme (Nika 2026-08-19: "set 'globalTheme' 'light' for
  // bellforge workspace by default - overriding users system preference").
  // Precedence, highest first: the user's own choice → this workspace's
  // setting → the OS. The decision itself is a pure, separately tested
  // function in shared/functions/themeMode.js; this only carries it out.
  //
  // ⚠️ A user who explicitly chose 'auto' keeps following their OS and is
  // NOT overridden — that is why the raw-key answer is passed rather than the
  // normalized mode, which cannot tell "chose auto" from "never chose".
  // ⚠️ Applied here, in the same deactivate-old/activate-new cycle as the
  // design system, so a switch to a workspace with no setting REVERTS rather
  // than leaking the previous workspace's theme.
  // Never persisted to the user record: this is a workspace default, not a
  // choice the user made.
  // ⚠️ PARKED 2026-08-19 — DO NOT RE-ARM WITHOUT READING THIS.
  //
  // This block is the prime suspect for the total freeze on the bellforge
  // workspace, and it is parked as the decisive test. The narrowing, from
  // two independent reproductions (mine and bellforge2's):
  //   • The wedge reproduces on the PLATFORM host
  //     (staging.my.symbols.app/w/bellforge/ws_UYAOhQyNjm), where no
  //     custom-domain code runs at all — so neither the branded surfaces nor
  //     the host→org resolver can be the cause.
  //   • It is a build regression: workspace.7df32db5.js rendered that URL
  //     (slow, ~30s, but it always arrived); workspace.a51d52c5.js wedges.
  //   • Of the six commits between those builds, this one (70dd21090) is the
  //     ONLY one whose code executes on the platform host. Every other commit
  //     in the range touches whitelabel-only or test-only files.
  //   • Before it, `applyWorkspaceConfig` NEVER called `changeGlobalTheme`.
  //     This added that call, and it fires only for a workspace that carries
  //     a `theme` setting — which, right now, is bellforge and nothing else.
  //     That is exactly why one workspace wedges and the others do not.
  //
  // The mechanism is a BLOCK, not a loop: `resolveWorkspaceThemeMode` is
  // convergent (its own tests cover apply→noop and revert→noop), so this does
  // not spin. But `changeGlobalTheme` re-applies the design system across the
  // whole app, smbls element preparation is super-linear in component size,
  // and the bellforge module is ~20k lines that already took ~30s to settle
  // BEFORE this. A few extra full re-applies on that tree is enough to turn a
  // slow boot into an unresponsive one.
  //
  // Parked, not deleted — the decision function, its tests, and the
  // precedence contract all stay. Nika asked for this default and it should
  // come back, but through the BOOT SEED (the same pre-mount path that
  // already sets themeMode/data-theme before anything mounts), not by
  // re-theming a mounted app. Re-arming is flipping this one constant, and
  // it must not be flipped until a build proves the freeze is gone without it.
  //
  // RE-ARMED AT THE BOOT SEED (2026-10-18, GreenIG "Dark theme by default"):
  // the workspace default now applies where this note asked — shared
  // bootResolve.js 5b (the warm mount, from its scope snapshot's cached
  // `workspaceTheme`) and 7c (the cold mount, from the full record's
  // `settings.theme`), both BEFORE create(). This mounted-app path stays
  // parked: an in-session switch into such a workspace keeps the current
  // theme until the next load.
  const WORKSPACE_DEFAULT_THEME_ENABLED = false

  try {
    // ⚠️ Record the RAW value BEFORE the park gate. bellforge2 rightly
    // pointed out that parking the feature would otherwise blind the one
    // check that tests whether it was ever guilty: with the gate applied
    // first, the trail would report "no theme" on every workspace by
    // construction and prove nothing. This label answers, on the host that
    // actually wedges, whether `cfg.theme` is even present.
    //
    // Their static reading says it is NOT: bellforge is a full-takeover
    // client module, so resolveWorkspaceConfig returns
    // `{...DEFAULT_CONFIG, ...project.config}` EARLY, never reaching the
    // `_normalizeSavedRow` branch that sets `theme` — and DEFAULT_CONFIG has
    // no theme key. They also read staging directly: `settings.theme` exists
    // on zero workspaces. If that holds, this block no-ops for bellforge and
    // my parking it fixed nothing. The remaining caveat is that the
    // PLATFORM's stored module config is authoritative at runtime and can
    // differ from the repo's config.js, which is exactly what this measures.
    const rawThemeCfg = cfg.theme
    shellBootTrail(
      'workspaceConfig:theme:cfg',
      rawThemeCfg && typeof rawThemeCfg === 'object'
        ? `mode=${rawThemeCfg.mode}`
        : `absent:${typeof rawThemeCfg}`
    )
    const wsThemeMode =
      WORKSPACE_DEFAULT_THEME_ENABLED && rawThemeCfg && typeof rawThemeCfg === 'object'
        ? rawThemeCfg.mode
        : null
    const decision = shellResolveWorkspaceThemeMode({
      userChoseExplicitly: shellUserHasOwnThemeChoice({
        rawStoredKeyAbsent: shellHasNoStoredThemeMode(),
        userRecordMode: root && root.userPrefs ? root.userPrefs.themeMode : null
      }),
      workspaceMode: wsThemeMode,
      appliedWorkspaceMode: _appliedWorkspaceTheme
    })
    shellBootTrail(
      decision && decision.apply
        ? 'workspaceConfig:theme:apply'
        : decision && decision.revert
          ? 'workspaceConfig:theme:revert'
          : 'workspaceConfig:theme:noop',
      wsThemeMode || '-'
    )
    if (decision && (decision.apply || decision.revert)) {
      // Revert falls back to the user's OWN mode — their record/local choice
      // if any, else 'auto' so the OS resumes deciding.
      const next = decision.apply || (root && root.themeMode) || 'auto'
      _appliedWorkspaceTheme = decision.apply || null
      // Dynamic import for the same reason wireGlobalListeners uses one: this
      // path must not drag the framework chunk into the eager bundle.
      import('smbls')
        .then(({ changeGlobalTheme }) => {
          if (typeof changeGlobalTheme === 'function') changeGlobalTheme(next)
        })
        .catch(() => {})
    }
  } catch (err) {
    // A theme default must never be able to break workspace activation.
    console.warn('[workspaceConfigs] workspace theme default skipped:', err && err.message)
  }

  // The shared language primitive owns translations and <html lang>
  // together. A separate attribute write here could contradict the visible
  // strings or race a lazy language delivery. Workspace resolution remains
  // transient, so it never records a personal/account preference.
  const languageElement = root.__element
  if (languageScopeReady && typeof languageElement?.call === 'function') {
    try {
      Promise.resolve(languageElement.call('applyWorkspaceLangPolicy')).catch((err) =>
        console.warn('[workspaceConfigs] workspace language policy failed:', err?.message || err)
      )
    } catch (err) {
      console.warn('[workspaceConfigs] workspace language policy failed:', err?.message || err)
    }
  }
  // Explicit layout direction remains a workspace layout setting.
  const rootEl = doc?.documentElement
  if (rootEl) {
    rootEl.setAttribute('dir', cfg.layoutDirection || 'ltr')
  }

  // Write the resolved (camelCase) settings back so reactive consumers see them.
  // HeaderTabs reads `navbar`; the home dashboard reads `home_default_panels`;
  // the apps menu reads `apps`. Preserve any other keys already on the bag, and
  // only overwrite home_default_panels/apps when the resolved config carries them
  // (so a workspace WITHOUT a project keeps its server dashboard-defaults).
  const nextSettings = {
    ...scopedSettings,
    navbar: cfg.navbar,
    designSystem: cfg.designSystem,
    language: cfg.language,
    languagePolicy: cfg.languagePolicy,
    layoutDirection: cfg.layoutDirection,
    modules: cfg.modules
  }
  if (cfg.home_default_panels !== undefined)
    nextSettings.home_default_panels = cfg.home_default_panels
  if (cfg.apps !== undefined) nextSettings.apps = cfg.apps
  // PROVENANCE stamp: which workspace this bag DESCRIBES (isolation audit
  // #10). `root.workspaceSettings` is a global key that lags a switch, while
  // its consumers key caches by the CURRENT `root.workspace.id` — so a reader
  // pairing a fresh scope with a stale bag wrote one workspace's panel set
  // under another's cache key (the wsPanels cross-workspace poison). Readers
  // (_resolveWsPanelsInfo) now trust the bag only when this stamp matches the
  // active scope. Written from the same source resolveWorkspaceConfig read.
  const _wsIdForBag = String(root.workspace?.id ?? root.workspace?._id ?? '') || null
  if (_wsIdForBag) nextSettings.workspace_id = _wsIdForBag
  // The bar THIS pass derived — see "DERIVED vs PERSONAL" above. Local only:
  // the consoles send the server only the keys they change.
  nextSettings._navbarDerived = cfg.navbar
  // No-op guard (boot F4): applyWorkspaceConfig is re-run by EVERY activation
  // trigger (boot chain, auth ensure, onWorkspaceChange, converge retries) and
  // unconditionally re-published the bag each time — re-rendering HeaderTabs,
  // the home dashboard and every other workspaceSettings consumer with
  // byte-identical data, all post-reveal. Skip the write when the recomputed
  // bag deep-equals what is already live; the DOM side effects above
  // (design-system vars, <html> lang/dir) are idempotent and already ran.
  if (shellDeepEqual(root.workspaceSettings, nextSettings)) return
  root.update({ workspaceSettings: nextSettings })
}
